import { useState, useEffect } from "react";
import { RightPanel } from "@/components/RightPanel";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { EvidenceCaptureItem } from "@/components/EvidenceCaptureItem";
import { NoticeItem } from "@/components/NoticeItem";
import { Badge } from "@/components/ui/badge";
import { Pencil, Plus, Flag, Loader2 } from "lucide-react";
import {
  URL,
  api,
  URLRescan,
  EvidencePackage,
  EscalationEmail,
  URLAgency,
} from "@/lib/api";

export interface URLDetailsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: URL | null;
}

export interface URLDetailsPanelContentProps {
  url: URL;
}

/**
 * URLDetailsPanelContent - Content-only component for URL details (no wrapper)
 * Use this with SplitLayout for side-by-side view
 */
export function URLDetailsPanelContent({ url }: URLDetailsPanelContentProps) {
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notes, setNotes] = useState("");
  const [rescans, setRescans] = useState<URLRescan[]>([]);
  const [evidencePackages, setEvidencePackages] = useState<EvidencePackage[]>(
    [],
  );
  const [escalationEmails, setEscalationEmails] = useState<EscalationEmail[]>(
    [],
  );
  const [agencies, setAgencies] = useState<URLAgency[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch rescans and evidence packages when URL changes
  useEffect(() => {
    if (!url) {
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch rescans, evidence packages, escalation emails, and agencies
        const [rescansData, evidenceData, emailsData, agenciesData] =
          await Promise.all([
            api.urls.getRescans(url.id, 10).catch((err) => {
              console.error("Error fetching rescans:", err);
              return [];
            }),
            api.evidencePackages
              .list({ originalUrl: url.url, limit: 10 })
              .catch((err) => {
                console.error("Error fetching evidence packages:", err);
                return [];
              }),
            api.escalationEmails.listForUrl(url.id, 10).catch((err) => {
              console.error("Error fetching escalation emails:", err);
              return [];
            }),
            api.urls.getAgencies(url.id).catch((err) => {
              console.error("Error fetching agencies:", err);
              return [];
            }),
          ]);

        setRescans(rescansData);
        setEvidencePackages(evidenceData);
        setEscalationEmails(emailsData);
        setAgencies(agenciesData);
      } catch (err) {
        console.error("Error fetching URL details:", err);
        setError("Failed to load URL details");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [url, open]);

  // Reset notes when URL changes
  useEffect(() => {
    setNotes("");
    setIsEditingNotes(false);
  }, [url?.id]);

  // Map escalation emails to notice items
  const noticesSent = escalationEmails.map((email) => ({
    id: email.id.toString(),
    recipient: email.recipient_type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" "),
    type: email.recipient_type.toLowerCase() as
      | "webmaster"
      | "hosting-provider"
      | "registrar"
      | string,
    date: email.sent_at || email.scheduled_at || email.created_at,
    status: (email.status.toLowerCase() === "sent"
      ? "sent"
      : email.status.toLowerCase() === "failed"
        ? "failed"
        : email.status.toLowerCase() === "bounced"
          ? "bounced"
          : "pending") as "sent" | "bounced" | "pending" | "failed",
  }));

  return (
    <>
      {/* URL Details Section */}
      <div className="pb-4 border-b border-gray-200">
        <div className="text-sm text-foreground break-all">
          {url.url}
        </div>
      </div>

      {/* Status and Case Info */}
      <div className="pt-4 pb-6 border-b border-gray-200 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Status</div>
            <StatusBadge status={url.status} />
          </div>
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Case</div>
            <div className="text-sm text-foreground">{url.case_name || "—"}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
              {agencies.length > 1 ? "Agencies" : "Agency"}
            </div>
            <div className="flex flex-wrap gap-1">
              {agencies.length > 0 ? (
                agencies.map((agency) => (
                  <Badge
                    key={agency.id}
                    variant="outline"
                    className="bg-blue-50 text-blue-800 border-blue-200"
                    title={agency.abbreviation || agency.name}
                  >
                    {agency.abbreviation || agency.name}
                  </Badge>
                ))
              ) : (
                <Badge
                  variant="outline"
                  className="bg-blue-50 text-blue-800 border-blue-200"
                >
                  {url.agency_name || "—"}
                </Badge>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Added By</div>
            <div className="text-sm text-foreground">{url.user_display_name || "—"}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Date Added</div>
            <div className="text-sm text-foreground">
              {url.case_created_at
                ? new Date(url.case_created_at).toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })
                : "—"}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Days Live</div>
            <div className="text-sm text-foreground">
              {url.days_live !== null && url.days_live !== undefined
                ? `${url.days_live} days`
                : "—"}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Notices Sent</div>
            <div className="text-sm text-foreground">{noticesSent.length}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Last Checked</div>
            <div className="text-sm text-foreground">
              {url.last_checked
                ? new Date(url.last_checked).toLocaleString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                : "Never"}
            </div>
          </div>
        </div>
      </div>

      {/* Notes Section */}
      <div className="pt-4 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">Notes</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditingNotes(!isEditingNotes)}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>
        </div>
        {isEditingNotes ? (
          <div className="space-y-2">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this URL..."
              className="min-h-[100px]"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => {
                  // TODO: Add API endpoint to save notes
                  setIsEditingNotes(false);
                }}
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setNotes("");
                  setIsEditingNotes(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
            {notes || "No notes added yet."}
          </div>
        )}
      </div>

      {/* Evidence Captures Section */}
      <div className="pt-6 pb-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">
            Evidence Captures ({rescans.length + evidencePackages.length})
          </h3>
          <Button variant="ghost" size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Add Evidence
          </Button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading evidence...
          </div>
        ) : error ? (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
            {error}
          </div>
        ) : rescans.length === 0 && evidencePackages.length === 0 ? (
          <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
            No evidence captures found for this URL.
          </div>
        ) : (
          <div className="space-y-2">
            {/* Show rescans as evidence captures */}
            {rescans.map((rescan) => (
              <EvidenceCaptureItem
                key={rescan.id}
                timestamp={rescan.rescan_timestamp}
                captureType="auto"
                status={rescan.status_changed ? "status changed" : "captured"}
                caseName={url.case_name || undefined}
                onClick={() => console.log("View rescan:", rescan.id)}
              />
            ))}
            {/* Show evidence packages */}
            {evidencePackages.map((pkg) => (
              <EvidenceCaptureItem
                key={pkg.id}
                timestamp={pkg.created_at}
                captureType={
                  pkg.discovery_source === "manual" ? "manual" : "auto"
                }
                status={pkg.site_status || "captured"}
                caseName={pkg.case_name}
                onClick={() => console.log("View evidence package:", pkg.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Notices Sent Section */}
      <div className="pt-6 pb-6 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Notices Sent ({noticesSent.length})
        </h3>
        <div className="space-y-2">
          {noticesSent.map((notice) => (
            <NoticeItem
              key={notice.id}
              recipient={notice.recipient}
              recipientType={notice.type}
              sentDate={notice.date}
              status={notice.status}
              onClick={() => console.log("View notice:", notice.id)}
            />
          ))}
        </div>
      </div>

      {/* Flags Section */}
      <div className="pt-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Flags</h3>
        <div className="space-y-2">
          <Button variant="outline" className="w-full justify-start" size="sm">
            <Flag className="h-4 w-4 mr-2" />
            Flag to Engineering
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start text-destructive hover:text-destructive"
            size="sm"
          >
            Request Manual Takedown
          </Button>
        </div>
      </div>
    </>
  );
}

/**
 * URLDetailsPanel - Complete right panel for URL details with Sheet wrapper
 *
 * Usage:
 * <URLDetailsPanel
 *   open={isPanelOpen}
 *   onOpenChange={setIsPanelOpen}
 *   url={selectedUrl}
 * />
 */
export function URLDetailsPanel({
  open,
  onOpenChange,
  url,
}: URLDetailsPanelProps) {
  if (!url) {
    return null;
  }

  return (
    <RightPanel open={open} onOpenChange={onOpenChange} title="URL Details">
      <URLDetailsPanelContent url={url} />
    </RightPanel>
  );
}
